import { useLayoutEffect, useRef, useState } from 'react';
import { useCuttingStore } from './state/useCuttingStore';
import { ImportFormatDialog } from './dialogs/ImportFormatDialog';
import { countOffcuts, offcutRowHasError, totalOffcutArea } from './model/offcutsUtils';
import { NumericKeyboard } from './keyboard/NumericKeyboard';
import { nextCell, type CellDir } from './keyboard/cellNav';
import { useCoarsePointer } from './keyboard/useCoarsePointer';
import { usePinchZoom } from './gestures/usePinchZoom';
import { useMediaQuery } from '@/hooks/useMediaQuery';

/** Числовые поля остатка, привязанные к цифровой клавиатуре (.kb-input). */
type NumField = 'length' | 'width' | 'qty';

/**
 * Block 14 «Раскрой» — вкладка «Остатки» (PR #24, ADR-0081 редизайн модели).
 *
 * Таблица Остатков = СКЛАД (постоянный инвентарь устройства между заказами,
 * cross-project, localStorage `pomoshnik_offcuts_v1`; reactive-зеркало
 * `useCuttingStore.warehouse`). «+ Добавить» / правка строки / «× Удалить» =
 * операции над складом (удаление — ПОЛНОЕ из памяти). Колонка-чекбокс «участвует»
 * отмечает, какие остатки идут в текущий раскрой (per-project выбор
 * `document.participatingOffcutIds`, durable через ProjectData, ADR-0081).
 *
 * Прежняя project-local модель (`doc.offcuts` + «Сохранить в склад» /
 * «Использовать остатки» + `UseLeftoversModal`) упразднена (источник багов дубля/
 * рассинхрона). «Сохранить новые остатки из результата в склад» — PR #24-B.
 *
 * Структурно зеркало `PartsTab`: freeze-header / 2D-скролл / pinch-zoom из общих
 * `.sc-table`/`.sc-table-wrap`. Цифровая клавиатура — ТОЛЬКО на touch (coarse).
 */

export function OffcutsTab() {
  const warehouse = useCuttingStore((s) => s.warehouse);
  const participatingIds = useCuttingStore((s) => s.document.participatingOffcutIds);
  const addOffcut = useCuttingStore((s) => s.addOffcut);
  const updateOffcut = useCuttingStore((s) => s.updateOffcut);
  const removeOffcut = useCuttingStore((s) => s.removeOffcut);
  const toggleParticipation = useCuttingStore((s) => s.toggleOffcutParticipation);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'import' | null>(null);

  // CUT-2.4 паттерн: экранная клавиатура ТОЛЬКО на touch (coarse pointer);
  // десктоп (fine pointer) использует физическую клавиатуру.
  const [activeCell, setActiveCell] = useState<{ id: string; field: NumField } | null>(null);
  const coarse = useCoarsePointer();

  // PR #8b паттерн: Excel-модель таблицы — pinch-zoom размером ячеек только на
  // мобильном (≤480px). Freeze-header/2D-скролл — нативные из .sc-table-wrap.
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const isMobile = useMediaQuery('(max-width: 480px)');
  usePinchZoom({ viewportRef: tableWrapRef, contentRef: tableRef, enabled: isMobile });

  const handleAdd = () => {
    const o = addOffcut();
    setSelectedId(o.id);
    setFocusId(o.id);
  };

  const handleRemove = () => {
    if (selectedId) {
      removeOffcut(selectedId); // полное удаление из склада (+ снятие из выбора)
      setSelectedId(null);
    }
  };

  const toNum = (v: string): number => {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? 0 : Math.max(0, n); // отрицательные → 0 (неполный остаток)
  };

  /** Записать числовое поле остатка (qty ≥ 1; длина/ширина ≥ 0). Общий путь для
      инпутов и цифровой клавиатуры (без расхождения логики). */
  const setField = (id: string, field: NumField, raw: string) => {
    if (field === 'qty') {
      const v = parseInt(raw, 10);
      updateOffcut(id, { qty: Number.isNaN(v) ? 1 : Math.max(1, v) });
    } else {
      updateOffcut(id, { [field]: toNum(raw) });
    }
  };

  // ── Цифровая клавиатура (CUT-2.4) ──────────────────────────────────────
  const KB_FIELDS: NumField[] = ['length', 'width', 'qty'];

  /** Строка активного поля для клавиатуры (пустые длина/ширина = '', не '0'). */
  const fieldString = (id: string, field: NumField): string => {
    const o = warehouse.find((x) => x.id === id);
    if (!o) return '';
    const v = o[field];
    if (field === 'qty') return String(v);
    return v > 0 ? String(v) : '';
  };

  const focusCell = (id: string, field: NumField) => {
    setActiveCell({ id, field });
  };

  /** Активна ли ячейка (явная подсветка `.cell-active` — работает на клик И на
      программную стрелочную навигацию; зеркало PartsTab, A.4). */
  const isCellActive = (id: string, field: NumField) =>
    activeCell?.id === id && activeCell.field === field;

  /** Снять подсветку при уходе фокуса ВНЕ таблицы (desktop). На touch `activeCell`
      управляет экранной клавиатурой — чистится через её `onClose`. */
  const handleTableBlur = (e: React.FocusEvent<HTMLTableElement>) => {
    if (coarse) return;
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setActiveCell(null);
  };

  // 2a desktop-навигация (зеркало PartsTab): фокус целевого инпута по data-атрибутам,
  // скоуплено своей таблицей (`tableRef`) — без коллизий с одинаковыми aria-label.
  const pendingFocus = useRef<{ id: string; field: NumField } | null>(null);
  const focusInput = (rowId: string, field: NumField) => {
    const el = tableRef.current?.querySelector<HTMLInputElement>(
      `[data-row-id="${CSS.escape(rowId)}"][data-field="${field}"]`,
    );
    el?.focus();
    el?.select();
  };
  useLayoutEffect(() => {
    const pf = pendingFocus.current;
    if (pf) {
      pendingFocus.current = null;
      focusInput(pf.id, pf.field);
    }
  });

  /** Навигация экранной клавиатуры (touch). Геометрия — общая `nextCell`. */
  const handleKbNavigate = (dir: CellDir) => {
    if (!activeCell) return;
    const rowIdx = warehouse.findIndex((o) => o.id === activeCell.id);
    const colIdx = KB_FIELDS.indexOf(activeCell.field);
    if (rowIdx === -1 || colIdx === -1) return;
    const nx = nextCell(rowIdx, colIdx, dir, KB_FIELDS.length, warehouse.length);
    if (nx.kind === 'none') return;
    const field = KB_FIELDS[nx.col] ?? 'length';
    if (nx.kind === 'append') {
      const no = addOffcut();
      setActiveCell({ id: no.id, field });
      return;
    }
    const target = warehouse[nx.row];
    if (target) setActiveCell({ id: target.id, field });
  };

  /** 2a: навигация физической клавиатурой (desktop, Excel-стиль) — зеркало PartsTab.
      ↑/↓ — соседний ряд той же колонки; ←/→ — соседняя ячейка ВСЕГДА (независимо от
      позиции каретки — решение владельца 15.06.2026, как в PartsTab); ↓ за последний
      ряд → новый остаток + фокус. */
  const handleCellKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    rowId: string,
    field: NumField,
  ) => {
    const dirMap: Record<string, CellDir> = {
      ArrowUp: 'up',
      ArrowDown: 'down',
      ArrowLeft: 'left',
      ArrowRight: 'right',
    };
    const dir = dirMap[e.key];
    if (!dir) return;
    const rowIdx = warehouse.findIndex((o) => o.id === rowId);
    const colIdx = KB_FIELDS.indexOf(field);
    if (rowIdx === -1 || colIdx === -1) return;
    const nx = nextCell(rowIdx, colIdx, dir, KB_FIELDS.length, warehouse.length);
    if (nx.kind === 'none') {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    const targetField = KB_FIELDS[nx.col] ?? 'length';
    if (nx.kind === 'append') {
      const no = addOffcut();
      setActiveCell({ id: no.id, field: targetField }); // подсветка новой строки
      pendingFocus.current = { id: no.id, field: targetField };
      return;
    }
    const target = warehouse[nx.row];
    if (target) {
      setActiveCell({ id: target.id, field: targetField }); // подсветка идёт за стрелками
      focusInput(target.id, targetField);
    }
  };

  return (
    <div>
      <div className="sc-section-title">Остатки (склад)</div>

      {/* Тулбар. «Скан QR» disabled — камера/QR = отдельная задача (B-224). */}
      <div className="sc-toolbar">
        <button
          type="button"
          data-tb="oqr"
          className="btn btn-secondary btn-sm"
          disabled
          title="Сканер QR-этикеток появится позже"
        >
          📷 Скан QR
        </button>
        <button type="button" data-tb="oadd" className="btn btn-success btn-sm" onClick={handleAdd}>
          + Добавить остаток
        </button>
        <button
          type="button"
          data-tb="odel"
          className="btn btn-danger btn-sm"
          onClick={handleRemove}
        >
          × Удалить остаток
        </button>
        <button
          type="button"
          data-tb="oimport"
          className="btn btn-secondary btn-sm"
          onClick={() => setDialog('import')}
        >
          📥 Импорт
        </button>
      </div>

      {/* Таблица склада. Колонка «✓» — выбор «участвует в раскрое» (per-project). */}
      <div className="sc-table-wrap" ref={tableWrapRef}>
        <table className="sc-table" ref={tableRef} onBlur={handleTableBlur}>
          <thead>
            <tr>
              <th style={{ width: 36 }} title="Участвует в раскрое">
                ✂
              </th>
              <th style={{ width: 30 }}>#</th>
              <th>Длина</th>
              <th>Ширина</th>
              <th>Кол-во</th>
              <th>Имя/Партия</th>
              <th>Материал</th>
            </tr>
          </thead>
          <tbody>
            {warehouse.map((offcut, idx) => {
              const n = idx + 1;
              const participates = participatingIds.includes(offcut.id);
              const rowClass = [
                selectedId === offcut.id ? 'row-selected' : '',
                offcutRowHasError(offcut) ? 'row-error' : '',
                participates ? 'row-participates' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <tr
                  key={offcut.id}
                  className={rowClass || undefined}
                  onClick={() => setSelectedId(offcut.id)}
                >
                  <td className="cell-check">
                    <input
                      type="checkbox"
                      className="offcut-participate"
                      aria-label={`Участвует в раскрое ${n}`}
                      checked={participates}
                      onChange={() => toggleParticipation(offcut.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td className="sc-row-counter">{n}</td>
                  <td className="cell-num">
                    <input
                      type="text"
                      inputMode={coarse ? 'none' : 'numeric'}
                      className={`cell-input kb-input${isCellActive(offcut.id, 'length') ? ' cell-active' : ''}`}
                      aria-label={`Длина ${n}`}
                      data-row-id={offcut.id}
                      data-field="length"
                      value={offcut.length > 0 ? offcut.length : ''}
                      autoFocus={focusId === offcut.id}
                      onFocus={() => focusCell(offcut.id, 'length')}
                      onKeyDown={(e) => handleCellKeyDown(e, offcut.id, 'length')}
                      onChange={(e) => setField(offcut.id, 'length', e.target.value)}
                    />
                  </td>
                  <td className="cell-num">
                    <input
                      type="text"
                      inputMode={coarse ? 'none' : 'numeric'}
                      className={`cell-input kb-input${isCellActive(offcut.id, 'width') ? ' cell-active' : ''}`}
                      aria-label={`Ширина ${n}`}
                      data-row-id={offcut.id}
                      data-field="width"
                      value={offcut.width > 0 ? offcut.width : ''}
                      onFocus={() => focusCell(offcut.id, 'width')}
                      onKeyDown={(e) => handleCellKeyDown(e, offcut.id, 'width')}
                      onChange={(e) => setField(offcut.id, 'width', e.target.value)}
                    />
                  </td>
                  <td className="cell-small">
                    <input
                      type="text"
                      inputMode={coarse ? 'none' : 'numeric'}
                      className={`cell-input kb-input${isCellActive(offcut.id, 'qty') ? ' cell-active' : ''}`}
                      aria-label={`Количество ${n}`}
                      data-row-id={offcut.id}
                      data-field="qty"
                      value={offcut.qty}
                      onFocus={() => focusCell(offcut.id, 'qty')}
                      onKeyDown={(e) => handleCellKeyDown(e, offcut.id, 'qty')}
                      onChange={(e) => setField(offcut.id, 'qty', e.target.value)}
                    />
                  </td>
                  <td className="cell-text">
                    <input
                      type="text"
                      className="cell-input"
                      aria-label={`Имя или партия ${n}`}
                      placeholder="имя / партия"
                      value={offcut.name}
                      onChange={(e) => updateOffcut(offcut.id, { name: e.target.value })}
                    />
                  </td>
                  <td className="cell-text">
                    <input
                      type="text"
                      className="cell-input"
                      aria-label={`Материал ${n}`}
                      placeholder="материал"
                      value={offcut.material}
                      onChange={(e) => updateOffcut(offcut.id, { material: e.target.value })}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Футер: всего на складе + выбрано «участвуют». */}
      <div className="sc-table-footer">
        <span data-testid="offcuts-total">
          Остатков: <strong>{countOffcuts(warehouse)} шт</strong>
        </span>
        <span data-testid="offcuts-participating">
          Участвуют в раскрое: <strong>{participatingIds.length}</strong>
        </span>
        <span data-testid="offcuts-area">
          Суммарная площадь: <strong>{totalOffcutArea(warehouse).toFixed(2)} м.кв.</strong>
        </span>
      </div>

      {dialog === 'import' && <ImportFormatDialog onClose={() => setDialog(null)} />}

      {/* CUT-2.4: цифровая клавиатура — ТОЛЬКО на touch (coarse pointer). */}
      {coarse && activeCell && (
        <NumericKeyboard
          visible
          value={fieldString(activeCell.id, activeCell.field)}
          onChange={(v) => setField(activeCell.id, activeCell.field, v)}
          onNavigate={handleKbNavigate}
          onClose={() => setActiveCell(null)}
          allowDecimal
          allowFraction={false}
        />
      )}
    </div>
  );
}
